if (typeof ProductCardTemplate === 'undefined') {
  class ProductCardTemplate {
      static renderCard({
      product = {},
      finishData = {},
      finishIdToName = {},
      customerEmail = null,
      collectionHandle = null,
      activeFinish = null
    } = {}) {
      // Use global customer object if customerEmail not provided
      const customer = customerEmail !== null ? { email: customerEmail } : window.customer || {};

      return `
        <li class="product type-product">
          ${this.renderTrudoorChoice(product)}
          <a
            href="${this.getProductUrl(product, collectionHandle)}"
            class="woocommerce-LoopProduct-link woocommerce-loop-product__link"
          >
            ${this.renderProductImage(product, activeFinish, finishData)}
            ${this.renderProductTitle(product)}
            ${this.renderProductFinishes(product, finishData, finishIdToName)}
            ${this.renderProductPrice(product, customer.email)}
            ${this.renderLoginLink(product, customer.email)}
            ${this.renderUsage(product)}
          </a>
        </li>
      `;
    }
  
    static renderTrudoorChoice(product) {
      const isActive = product.meta?.custom?.trudoor_choice_active === "true" ||
                       product.meta?.custom?.trudoor_choice_active === true ||
                       product.trudoorChoiceActive === "true";
      
      if (!isActive) return "";
  
      const choiceText = product.meta?.custom?.trudoor_choice || 
                         product.trudoorChoiceText || "";
  
      return `
        <div class="trudoor-choice">
          <div class="main-color-bg">Trudoor Choice</div>
          ${choiceText ? `<span>${choiceText}</span>` : ""}
        </div>
      `;
    }
  
    static getProductUrl(product) {
      if (product.url) return product.url;
      return `/products/${product.handle}`;
    }
  
    static renderProductImage(product, activeFinish, finishData) {
      // Image priority logic:
      // 1. If finish filter is active: use product.image (variant-specific image)
      // 2. Default (no filter): use product.product_image (default variant image)
      // 3. Fallback: use product.image or featured_image

      let imageUrl;

      // Priority 1: When finish filter is active, use variant image
      if (activeFinish && product.image) {
        imageUrl = product.image;
      }
      // Priority 2: Default to product_image (default variant image)
      else if (product.product_image) {
        imageUrl = product.product_image;
      }
      // Priority 3: Fall back to image if product_image doesn't exist
      else if (product.image) {
        imageUrl = product.image;
      }
      // Priority 4: Final fallback to featured_image
      else {
        imageUrl = product.featured_image;
      }

      // console.log('[Image] Product:', product.handle, 'activeFinish:', activeFinish, 'Using image:', imageUrl);

      if (!imageUrl) {
        return `{{ "image" | placeholder_svg_tag: "product-card-no-img" }}`;
      }

      return `
        <img
          src="${imageUrl}"
          alt="${product.title}"
          class="attachment-woocommerce_thumbnail size-woocommerce_thumbnail wvs-attachment-image lazy loaded"
          loading="lazy"
          width="200"
          height="200"
          data-finish="${activeFinish || ''}"
        >
      `;
    }
  
    static renderProductTitle(product) {
      const vendorPart = product.vendor ? 
        `<strong style="display: inline;">${product.vendor}</strong> - ` : '';
      
      return `
        <h3 class="h2 woocommerce-loop-product__title">
          ${vendorPart}${product.title}
        </h3>
      `;
    }
  
    static renderProductFinishes(product, finishData, finishIdToName = {}) {
      // Debug flag - set to true to enable console logging
      const DEBUG_FINISHES = false;

      // Check multiple paths for finish data (Algolia vs Shopify API structures)
      const finishes = product.meta?.attribute?.finish ||
                       product.metafields?.finish ||
                       product.metafields?.attribute?.finish ||
                       [];

      if (DEBUG_FINISHES) {
        console.log('[Finish Debug] Product:', product.handle);
        console.log('[Finish Debug] Finishes raw:', finishes);
        console.log('[Finish Debug] FinishData keys (first 10):', Object.keys(finishData || {}).slice(0, 10));
        console.log('[Finish Debug] FinishData sample values:', Object.entries(finishData || {}).slice(0, 3));
        console.log('[Finish Debug] FinishIdToName keys (first 10):', Object.keys(finishIdToName || {}).slice(0, 10));
      }

      // Don't show finishes section if no finishes or only one finish exists
      if (!finishes || finishes.length <= 1) return "";

      // Build case-insensitive lookup map for finish names
      const finishDataLower = {};
      if (finishData) {
        for (const [key, value] of Object.entries(finishData)) {
          finishDataLower[key.toLowerCase()] = value;
        }
      }

      const swatchHtmlParts = finishes.map(finish => {
        let finishName = '';
        let swatchUrl = null;

        // Strategy 1: Finish is an object (from Liquid metaobject serialization)
        if (typeof finish === 'object' && finish !== null) {
          // Liquid may serialize metaobject references as objects with various properties:
          // - name: the display name
          // - swatch_image: the image object/URL
          // - id or system.id: the GID string
          finishName = finish.name || '';

          // First try direct swatch_image from the object
          if (finish.swatch_image) {
            // swatch_image might be an object with src, or a string URL
            swatchUrl = typeof finish.swatch_image === 'object'
              ? finish.swatch_image.src
              : finish.swatch_image;
          }

          // If no swatch_image, try lookup by GID (from id or system.id)
          if (!swatchUrl) {
            const gidString = finish.id || finish.system?.id;
            if (gidString) {
              const gidMatch = gidString.match(/\/(\d+)$/);
              if (gidMatch) {
                const numericId = gidMatch[1];
                swatchUrl = finishData?.[numericId];
                finishName = finishName || finishIdToName?.[numericId] || numericId;
              }
            }
          }

          // Fallback: lookup by name
          if (!swatchUrl && finishName) {
            swatchUrl = finishData?.[finishName] || finishDataLower?.[finishName.toLowerCase()];
          }

          if (DEBUG_FINISHES) {
            console.log('[Finish Debug] Object finish:', { finishName, swatchUrl, finish });
          }
        }
        // Strategy 2: Finish is a GID string (e.g., "gid://shopify/Metaobject/48032350364")
        else if (typeof finish === 'string') {
          // Check if it's a GID format
          const gidMatch = finish.match(/gid:\/\/shopify\/Metaobject\/(\d+)/);
          if (gidMatch) {
            const numericId = gidMatch[1];
            // Try lookup by numeric ID first, then full GID string
            swatchUrl = finishData?.[numericId] || finishData?.[finish];
            // Get the actual finish name from ID-to-name mapping, fallback to numeric ID
            finishName = finishIdToName?.[numericId] || numericId;

            if (DEBUG_FINISHES) {
              console.log('[Finish Debug] GID finish:', { numericId, finishName, swatchUrl, originalGid: finish });
            }
          } else {
            // It's a plain string (finish name like "US26D")
            finishName = finish;
            // Try exact match first, then case-insensitive
            swatchUrl = finishData?.[finish] || finishDataLower?.[finish.toLowerCase()];

            if (DEBUG_FINISHES) {
              console.log('[Finish Debug] String finish:', { finishName, swatchUrl });
            }
          }
        }

        // Skip if no valid swatchUrl or finishName
        // Check for null, undefined, "null", "undefined" strings
        if (!swatchUrl || swatchUrl === 'null' || swatchUrl === 'undefined' || !finishName) {
          if (DEBUG_FINISHES) {
            console.log('[Finish Debug] Skipping - no swatchUrl or finishName:', { finishName, swatchUrl, finish });
          }
          return '';
        }

        // Extra validation: ensure swatchUrl looks like a valid URL
        if (typeof swatchUrl !== 'string' || (!swatchUrl.startsWith('http') && !swatchUrl.startsWith('//'))) {
          if (DEBUG_FINISHES) {
            console.log('[Finish Debug] Skipping - invalid swatchUrl format:', { finishName, swatchUrl });
          }
          return '';
        }

        return `
          <img
            src="${swatchUrl}"
            class="swatch"
            alt="${finishName}"
            loading="lazy"
            width="10"
            height="10"
          >
        `;
      }).filter(html => html);

      // Only show "Multiple Finishes" section if we have valid swatch images
      // Prevents showing empty "Multiple Finishes" text with no swatches
      if (swatchHtmlParts.length === 0) return "";

      return `
        <div class="attr-text finish">
          Multiple Finishes
          <br>
          ${swatchHtmlParts.join('')}
        </div>
      `;
    }
  
    /**
     * Get the logged_in_price from the lowest-priced variant
     * This ensures we show the correct logged-in price that matches variants_min_price
     *
     * IMPORTANT: Algolia's "Show products instead of variants" setting picks a random variant
     * to represent the product. The `meta.custom.logged_in_price` may be from a different
     * variant than the one with `variants_min_price`. We need to validate that the
     * logged_in_price makes sense (should be LESS than the regular price).
     */
    static getLowestPriceVariantLoggedInPrice(product) {
      const variants = product.variants;

      // If we have a variants array, find the correct variant's logged_in_price
      if (variants && Array.isArray(variants) && variants.length > 0) {
        // Get the minimum price to match against
        const minPrice = parseFloat(String(product.variants_min_price || product.price_min || product.price || 0).replace(/,/g, ''));

        // Find the variant that matches the minimum price
        for (const variant of variants) {
          const variantPrice = parseFloat(String(variant.price || 0).replace(/,/g, ''));
          if (Math.abs(variantPrice - minPrice) < 0.01 && variant.logged_in_price) {
            return variant.logged_in_price;
          }
        }

        // Fallback: find the variant with the lowest price that has a logged_in_price
        let bestVariant = null;
        let bestPrice = Infinity;
        for (const variant of variants) {
          if (variant.logged_in_price) {
            const variantPrice = parseFloat(String(variant.price || 0).replace(/,/g, ''));
            if (variantPrice < bestPrice) {
              bestPrice = variantPrice;
              bestVariant = variant;
            }
          }
        }

        if (bestVariant) {
          return bestVariant.logged_in_price;
        }
      }

      // No variants array (Algolia data)
      // Check for lowest_logged_in_price (product-level metafield)
      const lowestLoggedInPrice = product.meta?.custom?.lowest_logged_in_price || product.lowest_logged_in_price;
      if (lowestLoggedInPrice) {
        return lowestLoggedInPrice;
      }

      // Fallback to meta.custom.logged_in_price
      // NOTE: Algolia may index a random variant's logged_in_price, not necessarily
      // the lowest-priced variant. Once logged_in_price_min is indexed, use that instead.
      const loggedInPrice = product.meta?.custom?.logged_in_price || product.logged_in_price;
      return loggedInPrice || null;
    }

    static renderProductPrice(product, customerEmail) {
      const isCutToSize = product.tags?.includes("Cut-to-Size");
      const isNonEcommerce = product.tags?.includes("Non-ecommerce") ||
                             product.tags?.includes("non-ecommerce");

      // FIXED: Get logged-in price from the LOWEST PRICE variant, not just any variant
      // This ensures we show the correct price that matches variants_min_price
      const loggedInPrice = this.getLowestPriceVariantLoggedInPrice(product);

      let priceHtml = '';
      let startingAt = 'Starting at: ';

      if (isCutToSize) {
        const price = product.variants_min_price || product.price_min || product.price;
        priceHtml = `<span class="woocommerce-Price-amount amount">$${this.formatPrice(price)} sq. ft.</span>`;
      } else if (isNonEcommerce && product.price === 0) {
        priceHtml = `<span class="woocommerce-Price-amount amount">Inquire</span>`;
        startingAt = '';
      } else if (loggedInPrice && customerEmail) {
        // Match Liquid implementation (trudoor-price.liquid)
        // - Strike through the REGULAR price (product.variants_min_price or product.price)
        // - Show the LOGGED-IN price as the actual price (not struck)
        const regularPrice = product.variants_min_price || product.price_min || product.price || '0.00';
        priceHtml = `
          <span class="woocommerce-Price-amount amount striked">
            <s>$${this.formatPrice(regularPrice)}</s>
          </span>
          <span class="woocommerce-Price-amount amount">$${this.formatPrice(loggedInPrice)}</span>
        `;
      } else {
        const price = product.variants_min_price || product.price_min || product.price || 0;
        priceHtml = `<span class="woocommerce-Price-amount amount">$${this.formatPrice(price)}</span>`;
      }

      return `
        <span class="price">
          ${startingAt}
          ${priceHtml}
        </span>
      `;
    }
  
    static renderLoginLink(product, customerEmail) {
      // Use the same logic as renderProductPrice to check for logged-in price
      const loggedInPrice = this.getLowestPriceVariantLoggedInPrice(product);

      // Only show login link if:
      // - Product has a logged-in price for the lowest-priced variant
      // - Customer is NOT logged in (no customerEmail)
      if (!loggedInPrice || customerEmail) return "";

      const currentPath = window.location.pathname;
      const returnParam = `?return_to=${encodeURIComponent(currentPath)}`;

      return `
        <div id="login-link" class="login-link">
          <a id="login-modal-link" href="${window.routes?.account_login_url || '/account/login'}${returnParam}">
            Log In
          </a>
          <span>or</span>
          <a id="register-modal-link" href="${window.routes?.account_register_url || '/account/register'}${returnParam}">
            Create Account
          </a>
          <span>for Better Price</span>
        </div>
      `;
    }
  
    static renderUsage(product) {
      const usage = product.meta?.attribute?.usage || product.usage;
      
      if (!usage || (Array.isArray(usage) && usage.length === 0)) return "";
      
      const usageValue = Array.isArray(usage) ? usage[0] : usage;
      const cleanUsage = typeof usageValue === 'string' ? 
        usageValue.match(/(\w|\s)+/g)?.[0] || usageValue : usageValue;
      
      return `
        <div class="usage">
          Usage: <span>${cleanUsage}</span>
        </div>
      `;
    }
  
    static formatPrice(price) {
      if (typeof price === 'string') {
        price = parseFloat(price.replace(/,/g, ''));
      }
      if (isNaN(price)) return '0.00';
      return price.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
  }

  window.ProductCardTemplate = ProductCardTemplate;
}